import type { HttpClient } from '../http/HttpClient.ts';

export type NotificationKind =
  | 'assigned'
  | 'mentioned'
  | 'commented'
  | 'status_changed'
  | 'field_changed'
  | 'completed'
  | 'watcher_added'
  | 'due_soon'
  | 'sprint_started'
  | 'sprint_ended'
  | 'project_access'
  | 'agent_question'
  | 'deletion_requested'
  | 'decision_waiting'
  | 'decision_decided';

export interface Notification {
  id: string;
  kind: NotificationKind;
  /** Repeats folded into this one while it was unread ("3 new comments"). */
  count: number;
  createdAt: string;
  readAt: string | null;
  /** Null when the system did it (the sprint scheduler). */
  actor: { id: string; name: string } | null;
  /** The actor's agent that did it, if one did. */
  agent: { id: string; name: string } | null;
  project: { id: string; key: string; name: string };
  target:
    | {
        type: 'item';
        id: string;
        number: number;
        title: string;
        itemType: string;
        dueDate: string | null;
      }
    | { type: 'sprint'; id: string; name: string }
    | { type: 'page'; id: string; title: string }
    | { type: 'decision'; id: string; number: number; title: string }
    | { type: 'project'; id: string };
  excerpt: string | null;
}

export interface UnreadCount {
  orgId: string;
  slug: string;
  count: number;
}

export type ReadSelection =
  | { ids: string[] }
  | { all: true }
  | { target: { type: 'item' | 'sprint' | 'page' | 'decision'; id: string } };

/** The kinds a person can adjust (the server's CONFIGURABLE_KINDS). */
export type ConfigurableKind =
  | 'assigned'
  | 'mentioned'
  | 'commented'
  | 'status_changed'
  | 'completed'
  | 'due_soon'
  | 'sprint_started'
  | 'sprint_ended'
  | 'project_access'
  | 'decision_waiting'
  | 'decision_decided';

export interface Channels {
  inApp: boolean;
  email: boolean;
  push: boolean;
}

export interface PushDevice {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  endpointHash: string;
}

export interface NotificationSettings {
  kinds: ({ kind: ConfigurableKind; defaults: Channels } & Channels)[];
  projects: {
    id: string;
    key: string;
    name: string;
    color: string;
    org: { slug: string; name: string };
    muted: boolean;
  }[];
  timeZone: string;
  /** Null when the server has push turned off. */
  push: { publicKey: string; devices: PushDevice[] } | null;
  /** Email to this address stopped (it bounced, or a message was marked as spam), or null. */
  emailBlocked: { reason: 'bounce' | 'complaint'; since: string } | null;
}

const base = (org: string) => `/orgs/${encodeURIComponent(org)}/notifications`;

/** The inbox: unread counts across orgs, one org's notifications, marking them read. */
export class NotificationsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  unread(): Promise<{ orgs: UnreadCount[] }> {
    return this.#http.get('/notifications/unread');
  }

  list(
    org: string,
    { unread = false, before }: { unread?: boolean; before?: Notification } = {},
  ): Promise<{ notifications: Notification[]; more: boolean }> {
    const query = new URLSearchParams();
    if (unread) query.set('unread', 'true');
    if (before) query.set('before', `${new Date(before.createdAt).toISOString()}_${before.id}`);
    const qs = query.toString();
    return this.#http.get(`${base(org)}${qs ? `?${qs}` : ''}`);
  }

  markRead(org: string, which: ReadSelection): Promise<{ read: number }> {
    return this.#http.post(`${base(org)}/read`, which);
  }

  // ---- your settings -------------------------------------------------------------------

  settings(): Promise<NotificationSettings> {
    return this.#http.get('/me/notification-settings');
  }

  setChannels(kind: ConfigurableKind, change: Partial<Channels>): Promise<void> {
    return this.#http.put(`/me/notification-settings/kinds/${kind}`, change);
  }

  addPushDevice(device: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    label: string;
  }): Promise<{ device: PushDevice }> {
    return this.#http.post('/me/push-devices', device);
  }

  removePushDevice(id: string): Promise<void> {
    return this.#http.delete(`/me/push-devices/${encodeURIComponent(id)}`);
  }

  setMuted(projectId: string, muted: boolean): Promise<void> {
    return this.#http.put(`/me/notification-settings/projects/${encodeURIComponent(projectId)}`, {
      muted,
    });
  }

  setTimeZone(timeZone: string): Promise<void> {
    return this.#http.put('/me/time-zone', { timeZone });
  }

  // ---- unsubscribe links (no sign-in) ----------------------------------------------------

  unsubscribePreview(
    token: string,
  ): Promise<{ kinds: { kind: ConfigurableKind; name: string }[] }> {
    return this.#http.post('/unsubscribe/preview', { token });
  }

  unsubscribe(token: string): Promise<void> {
    return this.#http.post('/unsubscribe', { token });
  }
}
